import { Check, TriangleAlert } from '@/components/icons'
import { Button } from '@/components/ui/button'

interface PlanTierCardProps {
  title: string
  price: string
  /** Small grey unit rendered after the price, e.g. "/month". */
  priceSuffix?: string
  features: string[]
  /** Small footnote under the features, e.g. "$2,388 billed annually · Save $480". */
  footnote?: React.ReactNode
  popular?: boolean
  /** Tier cannot be chosen (e.g. active aircraft exceed its cap). */
  disabled?: boolean
  /** Explanation rendered under the features when disabled. */
  disabledNote?: React.ReactNode
  ctaLabel: string
  ctaDisabled?: boolean
  onCheckout: () => void
}

export function PlanTierCard({
  title,
  price,
  priceSuffix,
  features,
  footnote,
  popular,
  disabled,
  disabledNote,
  ctaLabel,
  ctaDisabled,
  onCheckout,
}: PlanTierCardProps) {
  return (
    <div
      className={`relative flex h-full w-full flex-col gap-3 rounded-2xl border-2 px-6 pb-6 pt-8 text-left ${
        popular && !disabled
          ? 'border-content-brand-bold bg-background-brand-subtle'
          : 'border-border-L1 bg-surface-L0'
      }`}
    >
      {popular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-lg bg-content-brand-bold px-2 py-1 text-xs font-semibold leading-4 text-content-primary-white">
          Popular
        </span>
      )}
      <div className={`flex w-full flex-col gap-1 border-b border-border-L2 pb-4 ${disabled ? 'opacity-50' : ''}`}>
        <span className="text-base font-semibold leading-6 text-content-brand-bold">{title}</span>
        <span className="flex items-end whitespace-nowrap">
          <span className="text-[30px] font-semibold leading-none tracking-[-0.3px] text-content-secondary-bold">
            {price}
          </span>
          {priceSuffix && (
            <span className="text-xs font-semibold leading-4 text-content-primary-subtle">
              {priceSuffix}
            </span>
          )}
        </span>
      </div>
      {features.map((feature) => (
        <span key={feature} className={`flex items-start gap-2 ${disabled ? 'opacity-50' : ''}`}>
          <Check className="size-4 shrink-0 text-content-primary-bold" />
          <span className="text-sm leading-5 text-content-primary-bold">{feature}</span>
        </span>
      ))}
      {footnote && (
        <span className={`text-xs font-semibold leading-4 text-content-primary-subtle ${disabled ? 'opacity-50' : ''}`}>
          {footnote}
        </span>
      )}
      {disabled && disabledNote && (
        <span className="flex items-start gap-2">
          <TriangleAlert className="size-4 shrink-0 text-content-brand-bold" />
          <span className="text-xs font-semibold leading-4 text-content-primary-subtle">
            {disabledNote}
          </span>
        </span>
      )}
      <Button
        variant="primary"
        className="mt-auto h-10 w-full rounded-lg bg-background-brand-bold"
        disabled={ctaDisabled}
        onClick={onCheckout}
      >
        {ctaLabel}
      </Button>
    </div>
  )
}
